import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import {
    useAcceptJobMutation,
    useDeclineJobMutation,
    useGetIndividualContractorQuery,
    useGetJobsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { Check, Clear, NavigateNext } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import React, { MouseEvent } from 'react';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import toast from '../../../shared/Toast';
import UserRole from '../../../../shared/AuthRoles';
import { useNavigate } from 'react-router';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomSmallListChip } from '../../../../layout/styledcomponents/CustomChip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { CustomIconButton } from '../../../../layout/styledcomponents/CustomIconButton';

const JobRequests = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const loggedInUser = UserRole();

    const { data: profile } = useGetIndividualContractorQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const setupComplete = profile?.abn ?? true;

    const navigate = useNavigate();

    const { data: jobs } = useGetJobsQuery({ status: 'pending' });

    const [acceptJob] = useAcceptJobMutation();
    const [declineJob] = useDeclineJobMutation();

    const handleAccept = async (
        e: MouseEvent<HTMLButtonElement>,
        id: number,
    ) => {
        try {
            e.stopPropagation();
            await acceptJob({
                id,
                assignee: { id: loggedInUser.sub },
            }).unwrap();

            toast.success('Job accepted');
        } catch (error) {
            toast.error('Error accepting job');
        }
    };

    const handleDecline = async (
        e: MouseEvent<HTMLButtonElement>,
        id: number,
    ) => {
        try {
            e.stopPropagation();
            await declineJob(id).unwrap();

            toast.success('Job declined');
        } catch (error) {
            toast.error('Error declining job');
        }
    };

    return (
        <div>
            <Typography variant='subHeading'>New Requests</Typography>
            {jobs && jobs.length > 0 && setupComplete ? (
                <List disablePadding>
                    {jobs.map((job: Job) => (
                        <ListItem
                            divider
                            disableGutters
                            onClick={() =>
                                navigate(
                                    `/jobs?selectedJob=${job.id}&provider-type=${ProviderType.Contractor}`,
                                )
                            }
                            sx={{ px: 0 }}
                        >
                            <ListItemButton
                                sx={{
                                    px: 0,
                                    py: 1.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box
                                            display='flex'
                                            alignItems='center'
                                            gap={1}
                                        >
                                            <Typography component='span'>
                                                {job.owner.name}
                                            </Typography>
                                            <CustomSmallListChip
                                                variant='filled'
                                                size='small'
                                                style={{
                                                    color: 'white',
                                                    marginTop: '-2px',
                                                    padding: isMobile
                                                        ? '1px 3px'
                                                        : '2px 4px',
                                                    fontSize: isMobile
                                                        ? '0.6rem'
                                                        : '0.75rem',
                                                    letterSpacing: '0.5px',
                                                    backgroundColor:
                                                        theme.palette.info.main,
                                                }}
                                                label='NEW'
                                            />
                                        </Box>
                                    }
                                    secondary={`${new Date(
                                        job.jobDate,
                                    ).toLocaleDateString(
                                        'en-AU',
                                    )} - ${job.services
                                        .map((service) =>
                                            camelCaseToTitleCase(service),
                                        )
                                        .join(', ')}`}
                                />
                                <ButtonGroup>
                                    {isMobile ? (
                                        <>
                                            <CustomIconButton
                                                color='primary'
                                                onClick={(
                                                    e: MouseEvent<HTMLButtonElement>,
                                                ) => handleAccept(e, job.id)}
                                            >
                                                <Check />
                                            </CustomIconButton>
                                            <CustomIconButton
                                                color='warning'
                                                onClick={(
                                                    e: MouseEvent<HTMLButtonElement>,
                                                ) => handleDecline(e, job.id)}
                                            >
                                                <Clear />
                                            </CustomIconButton>
                                        </>
                                    ) : (
                                        <>
                                            <CustomButton
                                                variant='outlined'
                                                startIcon={<Check />}
                                                color='primary'
                                                onClick={(
                                                    e: MouseEvent<HTMLButtonElement>,
                                                ) => handleAccept(e, job.id)}
                                            >
                                                Accept
                                            </CustomButton>
                                            <CustomButton
                                                variant='outlined'
                                                startIcon={<Clear />}
                                                color='warning'
                                                onClick={(
                                                    e: MouseEvent<HTMLButtonElement>,
                                                ) => handleDecline(e, job.id)}
                                            >
                                                Decline
                                            </CustomButton>
                                        </>
                                    )}
                                </ButtonGroup>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Stack sx={{ pt: 1, gap: 1 }}>
                    {!setupComplete && (
                        <Alert
                            severity='error'
                            action={
                                <CustomButton
                                    color='inherit'
                                    endIcon={<NavigateNext />}
                                    onClick={() =>
                                        navigate('/contractorProfile')
                                    }
                                >
                                    Complete setup
                                </CustomButton>
                            }
                        >
                            Finish setting up your profile to start receiving
                            new job requests.
                        </Alert>
                    )}
                    <Card variant='outlined'>
                        <Box
                            sx={{
                                p: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            No new requests.
                        </Box>
                    </Card>
                </Stack>
            )}
        </div>
    );
};

export default JobRequests;
